import Student from '../../model/Student';
import { Button, CircularProgress, Collapse, Divider, Grid, Stack, Typography, useTheme } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { UserDataText } from './UserPanel';
import { GridProps } from '@mui/material/Grid/Grid';
import OlympicsContext from '../../store/olympics-context';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { cleanUpHtml } from '../../utils/string-tools';
import { applyAutoOralFormatting } from '../../utils/oral-description';
import SnackbarContext from '../../store/snackbar-context';
import AuthContext from '../../store/auth-context';
import {
	createMyStage3AccessRequest,
	getMyStage3AccessStatus,
	getMyStage3DocumentStreamUrl,
	openMyStage3Access,
} from '../../api/content-api';
import StudentWorkViewer from './StudentWorkViewer';
import {
	parseCommentOverlayOffsetX,
	parseCommentOverlayOffsetY,
	parseCommentOverlayRotationDeg,
	parseCommentOverlayScale,
	parseHideOverlayHighlightBoxes,
	parseHidePdfAnnotationRender,
	parseViewerPageRotations,
} from '../../utils/stage3ViewerNotes';
import { formatFinalTitleForStudent } from '../../utils/finalTitleDisplay';
import { isSmallOlympicVariant } from '../../config/variant-profile';

const Row = (props: GridProps) => {
	return (
		<Grid
			item
			container
			justifyContent={'space-between'}
			{...props}
		>
			{props.children}
		</Grid>
	);
};

type Props = {
	student: Student | null;
};
const UserResults = ({ student }: Props) => {
	const { palette } = useTheme();
	const isSmallOlympic = isSmallOlympicVariant();
	const { olympics } = useContext(OlympicsContext);
	const { token, email } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const [expand, setExpand] = useState(true);
	const [accessState, setAccessState] = useState<any>(null);
	const [accessLoading, setAccessLoading] = useState(false);
	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerDocumentId, setViewerDocumentId] = useState<number | null>(null);
	const [viewerTitle, setViewerTitle] = useState('Podgląd pracy');
	const [viewerPageRotations, setViewerPageRotations] = useState<Record<number, number>>({});
	const [viewerCommentOffsetY, setViewerCommentOffsetY] = useState<Record<number, number>>({});
	const [viewerCommentOffsetYGlobal, setViewerCommentOffsetYGlobal] = useState<number | null>(null);
	const [viewerCommentOffsetX, setViewerCommentOffsetX] = useState<Record<number, number>>({});
	const [viewerCommentOffsetXGlobal, setViewerCommentOffsetXGlobal] = useState<number | null>(null);
	const [viewerCommentScaleX, setViewerCommentScaleX] = useState(100);
	const [viewerCommentScaleY, setViewerCommentScaleY] = useState(100);
	const [viewerHideOverlayHighlightBoxes, setViewerHideOverlayHighlightBoxes] = useState(false);
	const [viewerHidePdfAnnotationRender, setViewerHidePdfAnnotationRender] = useState(false);
	const [viewerCommentRotationDeg, setViewerCommentRotationDeg] = useState<Record<number, number>>({});
	const [oralDescriptionExpanded, setOralDescriptionExpanded] = useState(true);
	const oralDescriptionHtml = cleanUpHtml(applyAutoOralFormatting(student?.result?.secondBOralDescription || ''), {
		ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'br', 'p', 'ul', 'ol', 'li'],
		ALLOWED_ATTR: [],
	});

	const areResultsPublished =
		olympics?.firstStagePublished ||
		olympics?.secondAStagePublished ||
		olympics?.secondBStagePublished ||
		olympics?.thirdAStagePublished ||
		olympics?.thirdBStagePublished;
	const isDataReady = !!student && !!olympics;
	const isForeignDistrict = (student?.district?.name || '').toLowerCase() === 'zagranica';
	const firstStageQualified = !!student?.result?.firstStageQualified;
	const secondAStageQualified = !!student?.result?.secondAStageQualified;
	const secondBStageQualified = !!student?.result?.secondBStageQualified;
	const thirdAStageQualified = !!student?.result?.thirdAStageQualified;
	const showFirstStage = !!olympics?.firstStagePublished && !isForeignDistrict;
	const showSecondAStage = !!olympics?.secondAStagePublished && firstStageQualified;
	const showSecondBStage = !!olympics?.secondBStagePublished && secondAStageQualified;
	const showThirdAStage = !!olympics?.thirdAStagePublished && secondBStageQualified;
	const showThirdBStage = !!olympics?.thirdBStagePublished && thirdAStageQualified;
	const canonicalFinalTitle = (student?.result?.finalTitle || '').trim();
	const displayedFinalTitle = formatFinalTitleForStudent(canonicalFinalTitle, student?.personalTitle);
	/** Blok tylko gdy API zwraca niepusty `final_title` (np. Finalista/ka lub Laureat/ka po IIIB). Poniżej progu — pusty string, brak bloku. */
	const showFinalTitle = !!olympics?.thirdAStagePublished && !!secondBStageQualified && canonicalFinalTitle !== '';
	const hasVisibleSections =
		showFirstStage || showSecondAStage || showSecondBStage || showThirdAStage || showThirdBStage || showFinalTitle;
	const viewerUrl = useMemo(
		() => (viewerDocumentId ? getMyStage3DocumentStreamUrl(viewerDocumentId) : ''),
		[viewerDocumentId],
	);
	const renderWatermark = (templateRaw: string, values: Record<string, string>) => {
		const template = String(templateRaw || '').trim() || '{email}|{datetime}';
		const rendered = template.replace(/\{([a-z_]+)\}/gi, (_all, keyRaw: string) => {
			const key = String(keyRaw || '').toLowerCase();
			return String(values[key] || '');
		});
		const parts = rendered.split('|').filter((part) => part.replace(/\s/g, '').length > 0);
		return parts.length > 0 ? parts.join('|') : rendered;
	};
	const hasUnfinishedRequest = useMemo(() => {
		const status = String(accessState?.request?.status || '');
		return ['pending', 'collecting', 'ready', 'confirmed', 'notified', 'active', 'paper_only'].includes(status);
	}, [accessState]);
	const viewEnabled = Boolean(accessState?.view_enabled);
	const requestEnabled = Boolean(accessState?.request_enabled);
	const accessVerified = Boolean(accessState?.access_verified ?? false);
	const hasRequest = Boolean(accessState?.request?.id);
	const requestStatus = String(accessState?.request?.status || '');
	const canOpenAccess = ['notified', 'confirmed', 'ready'].includes(requestStatus) && accessVerified;
	const showOpenAccessButton = hasRequest && canOpenAccess;
	const showPendingOfficeVerification =
		!accessVerified &&
		hasRequest &&
		['pending', 'collecting', 'ready', 'confirmed', 'notified'].includes(requestStatus);
	const statusLabel = useMemo(() => {
		const status = String(accessState?.request?.status || '');
		const labels: Record<string, string> = {
			pending: 'Oczekuje',
			collecting: 'W kompletowaniu',
			ready: 'Gotowe do potwierdzenia',
			confirmed: 'Potwierdzone przez biuro',
			notified: 'Zaproszenie wysłane',
			active: 'Dostęp aktywny',
			expired: 'Dostęp wygasł',
			invitation_expired: 'Wygasło zaproszenie do otwarcia',
			paper_only: 'Praca papierowa - kontakt z biurem',
		};
		return labels[status] || 'Brak zgłoszenia';
	}, [accessState]);
	const docTypeLabel = (type: string) => {
		const labels: Record<string, string> = {
			original: 'Praca oryginalna',
			reviewed_scan: 'Praca sprawdzona',
			review_1: 'Recenzja 1',
			review_2: 'Recenzja 2',
			extra: 'Dokument dodatkowy',
		};
		return labels[type] || type;
	};

	const refreshAccessStatus = async () => {
		setAccessLoading(true);
		try {
			const response = await getMyStage3AccessStatus(token);
			setAccessState(response?.data || null);
		} catch (_e) {
			// no-op
		} finally {
			setAccessLoading(false);
		}
	};

	useEffect(() => {
		if (olympics?.thirdAStagePublished) {
			refreshAccessStatus();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [olympics?.thirdAStagePublished]);

	useEffect(() => {
		setOralDescriptionExpanded(!olympics?.thirdAStagePublished);
	}, [olympics?.thirdAStagePublished]);

	const createRequest = async () => {
		if (hasUnfinishedRequest) {
			setMsg({
				msg: 'Masz już aktywne zgłoszenie. Możesz złożyć kolejne po zakończeniu obecnego.',
				severity: 'warning',
			});
			return;
		}
		try {
			await createMyStage3AccessRequest(token);
			setMsg({ msg: 'Wysłano prośbę o dostęp do sprawdzonej pracy.', severity: 'success' });
			await refreshAccessStatus();
		} catch (error: any) {
			setMsg({ msg: error?.response?.data?.message || 'Nie udało się wysłać zgłoszenia.', severity: 'error' });
		}
	};

	const openAccess = async () => {
		try {
			await openMyStage3Access(token);
			setMsg({ msg: 'Dostęp został aktywowany.', severity: 'success' });
			await refreshAccessStatus();
		} catch (error: any) {
			setMsg({ msg: error?.response?.data?.message || 'Nie udało się aktywować dostępu.', severity: 'error' });
		}
	};

	return (
		<>
			<Grid
				item
				container
				justifyContent={'space-between'}
				mt={5}
				onClick={() => setExpand(!expand)}
				sx={{ cursor: 'pointer' }}
			>
				<Typography
					variant={'h4'}
					fontWeight={'500'}
					color={palette.primary.main}
				>
					Wyniki
				</Typography>
				{expand ? (
					<KeyboardArrowUpIcon sx={{ color: palette.primary.main }} />
				) : (
					<KeyboardArrowDownIcon sx={{ color: palette.primary.main }} />
				)}
			</Grid>
			<Divider
				variant={'fullWidth'}
				sx={{
					backgroundColor: palette.secondary.light,
					borderBottomWidth: '3px',
				}}
			/>
			<Collapse in={expand}>
				<Grid
					item
					container
					justifyContent={'start'}
					width={'100%'}
					gap={'24px'}
				>
					{!isDataReady ? (
						<Row>
							<Grid
								item
								container
								alignItems='center'
								gap='12px'
							>
								<CircularProgress size={18} />
								<UserDataText text={'Trwa odczyt danych'} />
							</Grid>
						</Row>
					) : (
						<>
							{!areResultsPublished && (
								<Row>
									<UserDataText text={'Nie opublikowano jeszcze wyników'} />
								</Row>
							)}
							{showFirstStage && (
								<>
									{(() => {
										const isApproved = student?.approved === true;

										// Jeśli uczeń nie jest zatwierdzony, nie pokazuj wyników
										if (!isApproved) {
											return (
												<>
													<Row>
														<UserDataText
															title={'Etap I'}
															text={''}
														/>
													</Row>
													<Row>
														<UserDataText
															title={'Punkty: '}
															text={'-'}
														/>
														<UserDataText
															title={'Zakwalifikowany do etapu II: '}
															text={'-'}
														/>
													</Row>
												</>
											);
										}

										// Normalne wyświetlanie wyników dla zatwierdzonych uczniów
										return (
											<>
												<Row>
													<UserDataText
														title={'Etap I'}
														text={''}
													/>
												</Row>
												<Row>
													<UserDataText
														title={'Punkty: '}
														text={`${student?.result?.firstStagePoints ?? 0}`}
													/>
													<UserDataText
														title={'Zakwalifikowany do etapu II: '}
														text={`${student?.result?.firstStageQualified ? 'Tak' : 'Nie'}`}
													/>
												</Row>
											</>
										);
									})()}
								</>
							)}
							{showSecondAStage && (
								<>
									<Divider variant={'fullWidth'} />
									<Row>
										<UserDataText
											title={'Etap II część pisemna'}
											text={''}
										/>
									</Row>
									<Row>
										<UserDataText
											title={'Część I: '}
											text={`${student?.result.secondAOneStagePoints} pkt`}
										/>
										<UserDataText
											title={'Część II: '}
											text={`${student?.result.secondATwoStagePoints} pkt`}
										/>
									</Row>
									<Row>
										<UserDataText
											title={'Numer tematu: '}
											text={`${student?.result.secondAStageTopic}`}
										/>
										<UserDataText
											title={'Zakwalifikowany do części ustnej: '}
											text={`${student?.result.secondAStageQualified ? 'Tak' : 'Nie'}`}
										/>
									</Row>
								</>
							)}
							{showSecondBStage && (
								<>
									<Divider variant={'fullWidth'} />
									<Row>
										<UserDataText
											title={'Etap II część ustna'}
											text={''}
										/>
									</Row>
									{!isSmallOlympic && (
										<>
											<Row>
												<UserDataText
													title={'Część I: '}
													text={`${student?.result.secondBOneStagePoints} pkt`}
												/>
												<UserDataText
													title={'Część II: '}
													text={`${student?.result.secondBTwoStagePoints} pkt`}
												/>
											</Row>
											<Row>
												<UserDataText
													title={'Część III: '}
													text={`${student?.result.secondBThreeStagePoints} pkt`}
												/>
												<UserDataText
													title={'Łącznie: '}
													text={`${student?.result.secondStageTotalPoints} pkt`}
												/>
											</Row>
										</>
									)}
									{isSmallOlympic && (
										<Row>
											<UserDataText
												title={'Punkty: '}
												text={`${student?.result.secondBThreeStagePoints} pkt`}
											/>
										</Row>
									)}
									{olympics?.secondBStageQualificationPublished && (
										<Row>
											<UserDataText
												title={'Zakwalifikowany do etapu III: '}
												text={`${student?.result.secondBStageQualified ? 'Tak' : 'Nie'}`}
											/>
										</Row>
									)}
									{isSmallOlympic && olympics?.secondBOralDescriptionPublished && oralDescriptionHtml && (
										<Row>
											<Grid
												item
												container
												xs={12}
											>
												<Stack sx={{ width: '100%' }}>
													<Stack
														direction='row'
														alignItems='center'
														gap={0.5}
													>
														<Typography sx={{ fontWeight: 600, color: palette.primary.main }}>
															Charakterystyka wypowiedzi
														</Typography>
														<Button
															variant='text'
															size='small'
															onClick={() => setOralDescriptionExpanded((prev) => !prev)}
															sx={{ minWidth: 0, px: 0.5, textTransform: 'none' }}
														>
															{oralDescriptionExpanded ? 'zwiń' : 'rozwiń'}
														</Button>
													</Stack>
													<Collapse in={oralDescriptionExpanded}>
														<Typography
															component='blockquote'
															sx={{
																color: palette.primary.main,
																fontSize: '16px !important',
																borderLeft: `4px solid ${palette.primary.main}`,
																margin: '8px 0 0 0',
																paddingLeft: '12px',
															}}
														>
															<span
																dangerouslySetInnerHTML={{
																	__html: oralDescriptionHtml,
																}}
															/>
														</Typography>
													</Collapse>
												</Stack>
											</Grid>
										</Row>
									)}
								</>
							)}
							{showThirdAStage && (
								<>
									<Divider variant={'fullWidth'} />
									<Row>
										<UserDataText
											title={'Etap III część pisemna'}
											text={''}
										/>
									</Row>
									<Row>
										<UserDataText
											title={'Punkty: '}
											text={`${student?.result.thirdAStagePoints} pkt`}
										/>
										<UserDataText
											title={'Temat: '}
											text={`${student?.result.thirdAStageTopic}`}
										/>
									</Row>
									<Row>
										<UserDataText
											title={'Zakwalifikowany do części ustnej: '}
											text={`${student?.result.thirdAStageQualified ? 'Tak' : 'Nie'}`}
										/>
									</Row>
									{viewEnabled && (
										<Row>
											<Grid
												item
												xs={12}
											>
												<Stack
													direction='row'
													gap={1}
													alignItems='center'
													flexWrap='wrap'
												>
													<Typography sx={{ color: palette.primary.main, fontWeight: 600 }}>
														Wgląd do sprawdzonej pracy:
													</Typography>
													{accessLoading && <CircularProgress size={16} />}
													<Typography sx={{ color: palette.primary.main }}>Status: {statusLabel}</Typography>
												</Stack>
												{showPendingOfficeVerification && (
													<Typography
														variant='body2'
														sx={{ mt: 0.8, color: palette.warning.dark }}
													>
														Komplet dokumentów oczekuje na potwierdzenie przez biuro.
													</Typography>
												)}
												{!requestEnabled && !hasRequest && (
													<Typography
														variant='body2'
														sx={{ mt: 0.8, color: palette.text.secondary }}
													>
														Zgłaszanie próśb o wgląd jest obecnie wyłączone.
													</Typography>
												)}
												<Stack
													direction='row'
													gap={1}
													mt={1}
													flexWrap='wrap'
												>
													{requestEnabled && (
														<Button
															size='small'
															variant='outlined'
															onClick={createRequest}
															disabled={hasUnfinishedRequest}
														>
															PROSZĘ O WGLĄD DO PRACY
														</Button>
													)}
													{showOpenAccessButton && (
														<Button
															size='small'
															variant='contained'
															onClick={openAccess}
														>
															Otwórz podgląd pracy
														</Button>
													)}
													{(accessState?.documents || []).map((doc: any) => (
														<Button
															key={doc.id}
															size='small'
															onClick={() => {
																setViewerDocumentId(doc.id);
																setViewerTitle(`Podgląd: ${docTypeLabel(String(doc.document_type || ''))}`);
																setViewerPageRotations(parseViewerPageRotations(doc?.notes));
																const co = parseCommentOverlayOffsetY(doc?.notes);
																setViewerCommentOffsetY(co.byPage);
																setViewerCommentOffsetYGlobal(co.global);
																const cox = parseCommentOverlayOffsetX(doc?.notes);
																setViewerCommentOffsetX(cox.byPage);
																setViewerCommentOffsetXGlobal(cox.global);
																const sc = parseCommentOverlayScale(doc?.notes);
																setViewerCommentScaleX(sc.x);
																setViewerCommentScaleY(sc.y);
																setViewerHideOverlayHighlightBoxes(parseHideOverlayHighlightBoxes(doc?.notes));
																setViewerHidePdfAnnotationRender(parseHidePdfAnnotationRender(doc?.notes));
																setViewerCommentRotationDeg(parseCommentOverlayRotationDeg(doc?.notes));
																setViewerOpen(true);
															}}
															disabled={requestStatus !== 'active' || !accessVerified}
														>
															Podgląd: {docTypeLabel(String(doc.document_type || ''))}
														</Button>
													))}
												</Stack>
											</Grid>
										</Row>
									)}
								</>
							)}
							{showThirdBStage && (
								<>
									<Divider variant={'fullWidth'} />
									<Row>
										<UserDataText
											title={'Etap III część ustna'}
											text={''}
										/>
									</Row>
									<Row>
										<UserDataText
											title={'Część I: '}
											text={`${student?.result.thirdBOneStagePoints} pkt`}
										/>
										<UserDataText
											title={'Część II: '}
											text={`${student?.result.thirdBTwoStagePoints} pkt`}
										/>
									</Row>
								</>
							)}
							{showFinalTitle && (
								<>
									<Divider variant={'fullWidth'} />
									<Row>
										<UserDataText
											title={'Uzyskany tytuł: '}
											text={displayedFinalTitle}
										/>
									</Row>
								</>
							)}
							{areResultsPublished && !hasVisibleSections && (
								<Row>
									<UserDataText text={'Brak opublikowanych wyników dla etapu, do którego uzyskano kwalifikację.'} />
								</Row>
							)}
						</>
					)}
				</Grid>
			</Collapse>
			<StudentWorkViewer
				open={viewerOpen}
				onClose={() => setViewerOpen(false)}
				documentId={viewerDocumentId}
				url={viewerUrl}
				title={viewerTitle}
				watermark={
					renderWatermark(String(accessState?.watermark_template || ''), {
						email: String(email || 'uczeń'),
						datetime: new Date().toLocaleString(),
						student_name: '',
						work_code: '',
						doc_type: '',
					}) || `${email || 'uczeń'} | ${new Date().toLocaleString()}`
				}
				token={token}
				pageRotations={viewerPageRotations}
				commentOffsetYByPage={viewerCommentOffsetY}
				commentOffsetYGlobal={viewerCommentOffsetYGlobal}
				commentOffsetXByPage={viewerCommentOffsetX}
				commentOffsetXGlobal={viewerCommentOffsetXGlobal}
				commentScaleXPercent={viewerCommentScaleX}
				commentScaleYPercent={viewerCommentScaleY}
				hideOverlayHighlightBoxes={viewerHideOverlayHighlightBoxes}
				hidePdfAnnotationRender={viewerHidePdfAnnotationRender}
				commentRotationDegByPage={viewerCommentRotationDeg}
				compatStreamUrlBuilder={(id) => `/wp-json/olympics/students/me/documents/${id}/stream-compat`}
				onAccessError={(message) => {
					setViewerOpen(false);
					setMsg({ msg: message || 'Brak aktywnego dostępu.', severity: 'warning' });
				}}
			/>
		</>
	);
};

export default UserResults;
